import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { useCallback, useContext, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { useIsPreviewMode } from './messages/useCurrentOrPreviewMessage';

/**
 * A custom hook to handle eviction of unread count / view metrics fields on message, board (and its ancestors).
 * This gets triggered on route change from where it is placed.
 *
 * @author Sravan Reddy
 */
export const useEvictMessageAndBoardMetrics = () => {
  const client = useApolloClient();
  const { contextMessage } = useContext(AppContext);
  const isPreviewMode = useIsPreviewMode();
  const { router } = useEndUserRoutes();

  const routeChangeStart = useCallback(
    (url: string) => {
      if (router.asPath !== url) {
        if (!contextMessage || isPreviewMode) {
          return;
        }
        const { read, conversation } = contextMessage;
        const board = contextMessage?.board;

        if (!read || conversation?.unreadReplyCount > 0) {
          [board, ...(board?.ancestors?.edges.map(edge => edge.node) || [])].forEach(node => {
            if (node) {
              client.cache.evict({
                id: client.cache.identify(node),
                fieldName: 'unreadMessagesCount'
              });
            }
          });
        }

        if (conversation?.unreadReplyCount > 0) {
          client.cache.evict({
            id: client.cache.identify(conversation),
            fieldName: 'unreadReplyCount'
          });
        }

        client.cache.evict({
          id: client.cache.identify(contextMessage),
          fieldName: 'metrics'
        });
      }
    },
    [client.cache, contextMessage, isPreviewMode, router.asPath]
  );

  useEffect(() => {
    router.events.on('routeChangeStart', routeChangeStart);

    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
    };
  }, [routeChangeStart, router.events]);
};
